import { useMemo, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { DocType, DocTypeField } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function DocTypes() {
  const { persona } = useAuth();
  const canAdmin = persona?.roles.includes('DOC_ADMIN') ?? false;
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (q) p.set('q', q);
    return `/api/v1/docs/types?${p}`;
  }, [q]);
  const listQ = useApiQuery<{ items: DocType[] }>(query);

  return (
    <div>
      <PageHeader
        title="Document types"
        subtitle="DocVault document types and their data schemas"
        actions={
          canAdmin ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New doc type
            </button>
          ) : undefined
        }
      />
      <div className="mb-4">
        <input className="field-input max-w-xs" placeholder="Search code or name…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No document types yet" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Name</th>
                <th className="table-head">Issuer MDA</th>
                <th className="table-head">Fields</th>
                <th className="table-head">Validity</th>
                <th className="table-head">Revocable</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((dt) => (
                <tr key={dt.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{dt.code}</td>
                  <td className="table-cell">{dt.name}</td>
                  <td className="table-cell text-xs">{dt.issuerMda}</td>
                  <td className="table-cell text-xs text-slate-500">{dt.schemaJson.fields.map((f) => f.key).join(', ')}</td>
                  <td className="table-cell text-xs text-slate-500">{dt.validityDays ? `${dt.validityDays} days` : 'no expiry'}</td>
                  <td className="table-cell">
                    <StatusBadge status={dt.revocable ? 'ACTIVE' : 'CLOSED'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [issuerMda, setIssuerMda] = useState('');
  const [validityDays, setValidityDays] = useState('');
  const [revocable, setRevocable] = useState(true);
  const [fields, setFields] = useState<DocTypeField[]>([{ key: '', label: '', type: 'string', required: true }]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const cleanFields = fields.filter((f) => f.key && f.label);
      if (cleanFields.length === 0) throw new Error('At least one field is required');
      await api.post('/api/v1/docs/types', {
        code,
        name,
        issuerMda,
        schemaJson: { fields: cleanFields },
        validityDays: validityDays ? Number.parseInt(validityDays, 10) : null,
        revocable,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : e instanceof Error ? e.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New document type" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code (UPPER_SNAKE)">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="GOOD_STANDING_CERT" />
          </Field>
          <Field label="Name">
            <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Certificate of Good Standing" />
          </Field>
          <Field label="Issuer MDA">
            <input className="field-input" value={issuerMda} onChange={(e) => setIssuerMda(e.target.value)} placeholder="SRC" />
          </Field>
          <Field label="Validity (days, blank = never expires)">
            <input className="field-input" type="number" min="1" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={revocable} onChange={(e) => setRevocable(e.target.checked)} />
          Revocable
        </label>
        <div>
          <span className="field-label">Fields</span>
          <div className="space-y-2">
            {fields.map((f, i) => (
              <div key={i} className="flex gap-2">
                <input
                  className="field-input"
                  placeholder="key"
                  value={f.key}
                  onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))}
                />
                <input
                  className="field-input"
                  placeholder="label"
                  value={f.label}
                  onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                />
                <select
                  className="field-input max-w-[110px]"
                  value={f.type}
                  onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, type: e.target.value as DocTypeField['type'] } : x)))}
                >
                  <option value="string">string</option>
                  <option value="number">number</option>
                  <option value="date">date</option>
                </select>
                <label className="flex items-center gap-1 whitespace-nowrap text-xs">
                  <input
                    type="checkbox"
                    checked={f.required}
                    onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))}
                  />
                  required
                </label>
                <button className="btn-secondary !px-2 text-xs" onClick={() => setFields(fields.filter((_, j) => j !== i))} disabled={fields.length === 1}>
                  ✕
                </button>
              </div>
            ))}
            <button
              className="btn-secondary text-xs"
              onClick={() => setFields([...fields, { key: '', label: '', type: 'string', required: true }])}
            >
              + Add field
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !code || !name || !issuerMda}>
            {busy ? 'Saving…' : 'Save doc type'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
